import { Button, DropdownMenu, Heading, IconButton, Tag } from '@humanforce/mango-ui-react';
import type { PayRunDetailDto } from './payRunsApiTypes';
import type { PayRunTimelineAction, PayRunTimelineActionVerb } from './payRunTimelineModel';
import type { PayRunStatusTag } from './payRunNoticeModel';
import { NOT_IMPLEMENTED_HINT } from './PayRunNotImplemented';
import './PayRunDetailTitle.css';

interface PayRunDetailTitleProps {
  payRun: PayRunDetailDto;
  /** Lifecycle tag shown beside the operating state, e.g. "Awaiting approval". */
  statusTag?: PayRunStatusTag | null;
  /** Fail-closed: when the workflow/operating pair is unsafe, no header action may be offered. */
  suppressActions?: boolean;
  /** Renders the approver header: an "Approver view" tag and the approver's own actions. */
  approver?: {
    canApprove: boolean;
    busy?: boolean;
    onApprove?: () => void;
    onRequestChanges?: () => void;
  } | null;
  action?: PayRunTimelineAction | null;
  actionBusy?: boolean;
  onAction?: (verb: PayRunTimelineActionVerb) => void;
  secondaryActions?: PayRunTitleSecondaryAction[];
  onSecondaryAction?: (verb: PayRunTitleSecondaryAction['verb']) => void;
  onOpenReports?: () => void;
}

export interface PayRunTitleSecondaryAction {
  verb: 'edit' | 'revert_to_trial' | 'unlock_to_compliance' | 'cancel';
  label: string;
  destructive?: boolean;
}

const date = (value: string): string =>
  new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`));

export const PayRunDetailTitle = ({
  payRun,
  statusTag = null,
  suppressActions = false,
  approver = null,
  action,
  actionBusy = false,
  onAction,
  secondaryActions = [],
  onSecondaryAction,
  onOpenReports,
}: PayRunDetailTitleProps) => {
  const operatingStateLabel = payRun.workflowStatus === 'cancelled'
    ? 'Cancelled'
    : payRun.operatingState === 'trial_pay'
      ? 'DRAFT - Trial pay'
      : payRun.operatingState === 'payroll_active' ? 'Active pay' : 'Status unavailable';

  // Completed runs have a server-backed payslip surface in the Reports tab.
  const payslipsLabel = payRun.workflowStatus === 'complete' ? 'View payslips' : 'Preview payslips';
  const hasCompletedOutputs = payRun.workflowStatus === 'complete' && Boolean(payRun.finalisation);

  return (
  <div className="pay-run-detail-title" data-testid="pay-run-detail-title">
    <div className="pay-run-detail-title__main mgo-stack mgo-gap-xs">
      <div className="mgo-cluster mgo-gap-s mgo-align-center">
        <Heading level={1}>{payRun.displayTitle}</Heading>
        {approver
          ? <Tag label="Approver view" tone="neutral" icon="show-on" testId="pay-run-detail-approver-tag" />
          : (
            <>
              <Tag
                label={operatingStateLabel}
                tone={payRun.operatingState === 'payroll_active' ? 'positive' : 'neutral'}
              />
              {statusTag && <Tag label={statusTag.label} tone={statusTag.tone} testId="pay-run-detail-status-tag" />}
            </>
          )}
      </div>
      <p className="mgo-text-s mgo-color-muted" style={{ margin: 0 }}>
        Pay period {date(payRun.periodStartDate)}–{date(payRun.periodEndDate)}
        {' '}· Payment date <strong>{date(payRun.paymentDate)}</strong>
        {payRun.eftReleaseDate && <> · EFT release <strong>{date(payRun.eftReleaseDate)}</strong></>}
      </p>
    </div>
    {approver && (
      <div className="pay-run-detail-title__actions mgo-cluster mgo-gap-s mgo-align-center">
        {/* Opens the designed dialog, which explains that the send-back it depicts has no API
            behind it. The dialog's own confirm action is the thing that stays disabled. */}
        <Button
          label="Request changes"
          variant="secondary"
          disabled={approver.busy}
          testId="pay-run-approver-request-changes"
          onClick={approver.onRequestChanges}
        />
        <Button
          label="Approve"
          disabled={!approver.canApprove || approver.busy}
          loading={approver.busy}
          testId="pay-run-approver-approve"
          onClick={approver.onApprove}
        />
      </div>
    )}
    {!suppressActions && !approver && (
    <div className="pay-run-detail-title__actions mgo-cluster mgo-gap-s mgo-align-center">
      <Button
        label={payslipsLabel}
        variant="secondary"
        icon="show-on"
        disabled={!hasCompletedOutputs}
        tooltip={hasCompletedOutputs ? undefined : NOT_IMPLEMENTED_HINT}
        testId="pay-run-detail-preview-payslips"
        onClick={onOpenReports}
      />
      <DropdownMenu
        testId="pay-run-detail-reports"
        trigger={<Button label="Reports" variant="secondary" />}
        items={hasCompletedOutputs
          ? [{ label: 'Payment outputs and payslips', onClick: onOpenReports }]
          : [{ label: 'Reports are not available yet', disabled: true }]}
      />
      {secondaryActions.length > 0 && (
        <DropdownMenu
          testId="pay-run-detail-secondary-actions"
          placement="bottom-end"
          trigger={<IconButton icon="more" label="More actions" variant="secondary" disabled={actionBusy} />}
          items={secondaryActions.map((item) => ({
            label: item.label,
            variant: item.destructive ? ('destructive' as const) : ('default' as const),
            disabled: actionBusy,
            onClick: () => onSecondaryAction?.(item.verb),
          }))}
        />
      )}
      {action && (
        <Button
          label={action.label}
          loading={actionBusy}
          disabled={actionBusy}
          testId="pay-run-detail-title-action"
          onClick={() => onAction?.(action.verb)}
        />
      )}
    </div>
    )}
  </div>
  );
};
